import React, { useState } from 'react';

import ProfileMenu from './ProfileMenu/ProfileMenu';
import { SupportModal } from '../../../../features/support/support';

import {
  Card,
  Toolbar,
  IconButton,
  Tooltip,
  Box,
  useMediaQuery,
  useTheme,
  Stack,
  Button,
} from '@mui/material';

import FullLogo from '../../../../assets/images/fullLogo.png';

import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import MenuIcon from '@mui/icons-material/Menu';

import { useAuth } from '../../../../context/AuthContext/AuthContext';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { usePromoTimer } from '../../../../pages/BuyChallenge/usePromoTimer';

interface HeaderProps {
  open: boolean;
  drawerWidth: number;
  handleDrawerOpen: () => void;
}

const Header: React.FC<HeaderProps> = ({
  open,
  drawerWidth,
  handleDrawerOpen,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation(['header']);

  // Support modal state
  const [openSupportModal, setOpenSupportModal] = useState(false);

  const handleOpenSupportModal = () => setOpenSupportModal(true);
  const handleCloseSupportModal = () => setOpenSupportModal(false);

  // ----------------------------------
  // Promo timer logic (shared hook)
  // ----------------------------------
  const { activePromo, promoTimeLeft, formatTime } = usePromoTimer();

  // Existing Christmas offer (UNCHANGED)
  const isChristmasOfferActive =
    activePromo === 'cyberMonday' && promoTimeLeft > 0;

  // ----------------------------------
  // NEW YEAR OFFER
  // Active from Dec 26 → Jan 15
  // Countdown visible ONLY during last 3 days (Jan 13 → 15)
  // ----------------------------------
  const isNewYearOfferActive =
    activePromo === 'newYear' && promoTimeLeft > 0;

  const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;
  const showNewYearTimer =
    isNewYearOfferActive && promoTimeLeft <= THREE_DAYS_MS;

  const newYearTimeLeftLabel = showNewYearTimer
    ? formatTime(promoTimeLeft)
    : '';

  if (!user) {
    return null;
  }

  return (
    <>
      <Card
        elevation={0}
        sx={{
          position: 'fixed',
          top: 0,
          left: open && !isMobile ? `${drawerWidth}px` : 0,
          width: open && !isMobile
            ? `calc(100% - ${drawerWidth}px)`
            : '100%',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          transition: (theme) =>
            theme.transitions.create(['left', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          [`@media (max-width:${theme.breakpoints.values.md}px)`]: {
            transition: 'none',
          },
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Left section: menu + logo */}
          <Stack direction="row" alignItems="center">
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                marginRight: 2,
                ...(open && !isMobile && { display: 'none' }),
              }}
            >
              <MenuIcon />
            </IconButton>

            <Box component={Link} to="/">
              <Box
                component="img"
                sx={{
                  height: '50px',
                  width: 'auto',
                  ...(open && { display: 'none' }),
                  marginLeft: '16px',
                }}
                alt="RaiseMyFunds logo"
                src={FullLogo}
              />
            </Box>
          </Stack>

          {/* Right section: promos + support + profile */}
          <Stack direction="row" alignItems="center" spacing={2}>
            {/* ------------------------------
                NEW YEAR OFFER BUTTON
                Visible for entire promo duration
                Countdown only on last 3 days
            ------------------------------ */}
            {isNewYearOfferActive && (
              <Button
                component={Link}
                to="/buy-challenge"
                variant="contained"
                sx={{
                  borderRadius: 1.5, // slightly squared look (same as Christmas)
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  px: 2,
                  py: 1,
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  boxShadow: '0 4px 10px rgba(0,0,0,0.18)',
                  backgroundColor: '#047857', // same green as Christmas
                  '&:hover': {
                    backgroundColor: '#065f46', // same hover as Christmas
                    boxShadow: '0 6px 14px rgba(0,0,0,0.22)',
                  },
                }}
              >
                <Box component="span">{t('HEADER.NEW_YEAR_OFFER')}</Box>

                {!isMobile && showNewYearTimer && newYearTimeLeftLabel && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: '0.75rem',
                      borderRadius: 999,
                      px: 1,
                      py: 0.25,
                      bgcolor: 'rgba(255,255,255,0.18)',
                      border: '1px solid rgba(255,255,255,0.35)',
                    }}
                  >
                    {newYearTimeLeftLabel}
                  </Box>
                )}
              </Button>
            )}
            {/* EXISTING CHRISTMAS OFFER (UNCHANGED) */}
            {isChristmasOfferActive && (
              <Button
                component={Link}
                to="/buy-challenge"
                variant="contained"
              >
                {t('HEADER.CHRISTMAS_OFFER')}
              </Button>
            )}

            <Tooltip title={t('BUTTON.BUTTON_CONTACT_SUPPORT')}>
              <IconButton onClick={handleOpenSupportModal}>
                <SupportAgentIcon />
              </IconButton>
            </Tooltip>

            <ProfileMenu />
          </Stack>
        </Toolbar>
      </Card>

      {/* Support modal */}
      <SupportModal
        openSupportModal={openSupportModal}
        handleClose={handleCloseSupportModal}
      />
    </>
  );
};

export default Header;
